import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/products";
import { INDIA_CITY_STATE_LIST } from "@/lib/indiaCities";
import { collection, doc, getDocs, limit, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { toast } from "sonner";
import { logEvent } from "@/lib/analytics";
import { Truck, ShieldCheck, CreditCard, Wallet, Building, Smartphone, Lock, UserPlus, LogIn, Minus, Plus } from "lucide-react";

const CHECKOUT_DRAFT_KEY = "noteconsole_checkout_draft_v1";
const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const normalizeText = (value = "") => value.trim().toLowerCase();
const PROMO_COUPONS = [
  { code: "FREESHIP", label: "Free Delivery", description: "Free standard delivery", type: "free_shipping" },
  { code: "NEWUSER15", label: "New User 15", description: "15% off on your first order", type: "percent", discountPercent: 15, firstOrderOnly: true },
  { code: "NC10", label: "NC10", description: "10% off for all users", type: "percent", discountPercent: 10 },
];
const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const INDIAN_CITIES = [
  { city: "Ahmedabad", state: "Gujarat" },
  { city: "Bengaluru", state: "Karnataka", aliases: ["Bangalore"] },
  { city: "Bhopal", state: "Madhya Pradesh" },
  { city: "Bhubaneswar", state: "Odisha", aliases: ["Bhubaneshwar", "Bhubaneswar", "BBSR"] },
  { city: "Chandigarh", state: "Chandigarh" },
  { city: "Chennai", state: "Tamil Nadu", aliases: ["Madras"] },
  { city: "Cuttack", state: "Odisha" },
  { city: "Delhi", state: "Delhi", aliases: ["New Delhi"] },
  { city: "Faridabad", state: "Haryana" },
  { city: "Ghaziabad", state: "Uttar Pradesh" },
  { city: "Gurugram", state: "Haryana", aliases: ["Gurgaon"] },
  { city: "Guwahati", state: "Assam" },
  { city: "Hyderabad", state: "Telangana" },
  { city: "Indore", state: "Madhya Pradesh" },
  { city: "Jaipur", state: "Rajasthan" },
  { city: "Kanpur", state: "Uttar Pradesh" },
  { city: "Kochi", state: "Kerala", aliases: ["Cochin"] },
  { city: "Kolkata", state: "West Bengal", aliases: ["Calcutta"] },
  { city: "Lucknow", state: "Uttar Pradesh" },
  { city: "Mumbai", state: "Maharashtra", aliases: ["Bombay"] },
  { city: "Nagpur", state: "Maharashtra" },
  { city: "Noida", state: "Uttar Pradesh" },
  { city: "Patna", state: "Bihar" },
  { city: "Pune", state: "Maharashtra" },
  { city: "Puri", state: "Odisha" },
  { city: "Raipur", state: "Chhattisgarh" },
  { city: "Ranchi", state: "Jharkhand" },
  { city: "Rourkela", state: "Odisha" },
  { city: "Surat", state: "Gujarat" },
  { city: "Thane", state: "Maharashtra" },
  { city: "Vadodara", state: "Gujarat", aliases: ["Baroda"] },
  { city: "Varanasi", state: "Uttar Pradesh", aliases: ["Banaras", "Benares", "Vara"] },
  { city: "Visakhapatnam", state: "Andhra Pradesh", aliases: ["Vizag"] },
  { city: "Agartala", state: "Tripura" },
  { city: "Agra", state: "Uttar Pradesh" },
  { city: "Ajmer", state: "Rajasthan" },
  { city: "Aligarh", state: "Uttar Pradesh" },
  { city: "Allahabad", state: "Uttar Pradesh", aliases: ["Prayagraj"] },
  { city: "Amravati", state: "Maharashtra" },
  { city: "Amritsar", state: "Punjab" },
  { city: "Asansol", state: "West Bengal" },
  { city: "Aurangabad", state: "Maharashtra", aliases: ["Chhatrapati Sambhajinagar"] },
  { city: "Bareilly", state: "Uttar Pradesh" },
  { city: "Belagavi", state: "Karnataka", aliases: ["Belgaum"] },
  { city: "Bhilai", state: "Chhattisgarh" },
  { city: "Bhiwandi", state: "Maharashtra" },
  { city: "Bikaner", state: "Rajasthan" },
  { city: "Bilaspur", state: "Chhattisgarh" },
  { city: "Bokaro Steel City", state: "Jharkhand", aliases: ["Bokaro"] },
  { city: "Coimbatore", state: "Tamil Nadu" },
  { city: "Davanagere", state: "Karnataka" },
  { city: "Dehradun", state: "Uttarakhand" },
  { city: "Dhanbad", state: "Jharkhand" },
  { city: "Durgapur", state: "West Bengal" },
  { city: "Erode", state: "Tamil Nadu" },
  { city: "Gaya", state: "Bihar" },
  { city: "Gorakhpur", state: "Uttar Pradesh" },
  { city: "Gwalior", state: "Madhya Pradesh" },
  { city: "Hisar", state: "Haryana" },
  { city: "Hubballi", state: "Karnataka", aliases: ["Hubli"] },
  { city: "Imphal", state: "Manipur" },
  { city: "Jabalpur", state: "Madhya Pradesh" },
  { city: "Jalandhar", state: "Punjab" },
  { city: "Jalgaon", state: "Maharashtra" },
  { city: "Jammu", state: "Jammu and Kashmir" },
  { city: "Jamnagar", state: "Gujarat" },
  { city: "Jamshedpur", state: "Jharkhand" },
  { city: "Jhansi", state: "Uttar Pradesh" },
  { city: "Jodhpur", state: "Rajasthan" },
  { city: "Kakinada", state: "Andhra Pradesh" },
  { city: "Kolhapur", state: "Maharashtra" },
  { city: "Kollam", state: "Kerala" },
  { city: "Kota", state: "Rajasthan" },
  { city: "Kozhikode", state: "Kerala", aliases: ["Calicut"] },
  { city: "Latur", state: "Maharashtra" },
  { city: "Ludhiana", state: "Punjab" },
  { city: "Madurai", state: "Tamil Nadu" },
  { city: "Mangaluru", state: "Karnataka", aliases: ["Mangalore"] },
  { city: "Meerut", state: "Uttar Pradesh" },
  { city: "Moradabad", state: "Uttar Pradesh" },
  { city: "Mysuru", state: "Karnataka", aliases: ["Mysore"] },
  { city: "Nanded", state: "Maharashtra" },
  { city: "Nashik", state: "Maharashtra" },
  { city: "Nellore", state: "Andhra Pradesh" },
  { city: "Panipat", state: "Haryana" },
  { city: "Rajahmundry", state: "Andhra Pradesh" },
  { city: "Rajkot", state: "Gujarat" },
  { city: "Saharanpur", state: "Uttar Pradesh" },
  { city: "Salem", state: "Tamil Nadu" },
  { city: "Sambalpur", state: "Odisha" },
  { city: "Shillong", state: "Meghalaya" },
  { city: "Shimla", state: "Himachal Pradesh" },
  { city: "Siliguri", state: "West Bengal" },
  { city: "Solapur", state: "Maharashtra" },
  { city: "Srinagar", state: "Jammu and Kashmir" },
  { city: "Thiruvananthapuram", state: "Kerala", aliases: ["Trivandrum"] },
  { city: "Thrissur", state: "Kerala" },
  { city: "Tiruchirappalli", state: "Tamil Nadu", aliases: ["Trichy"] },
  { city: "Tirupati", state: "Andhra Pradesh" },
  { city: "Tiruppur", state: "Tamil Nadu" },
  { city: "Udaipur", state: "Rajasthan" },
  { city: "Ujjain", state: "Madhya Pradesh" },
  { city: "Vellore", state: "Tamil Nadu" },
  { city: "Vijayawada", state: "Andhra Pradesh" },
  { city: "Warangal", state: "Telangana" },
  { city: "Yamunanagar", state: "Haryana" },
  { city: "Aizawl", state: "Mizoram" },
  { city: "Itanagar", state: "Arunachal Pradesh" },
  { city: "Kohima", state: "Nagaland" },
  { city: "Gangtok", state: "Sikkim" },
  { city: "Panaji", state: "Goa" },
  { city: "Port Blair", state: "Andaman and Nicobar Islands" },
  { city: "Puducherry", state: "Puducherry", aliases: ["Pondicherry"] },
  { city: "Silvassa", state: "Dadra and Nagar Haveli and Daman and Diu" },
  { city: "Daman", state: "Dadra and Nagar Haveli and Daman and Diu" },
  { city: "Leh", state: "Ladakh" },
  { city: "Kavaratti", state: "Lakshadweep" },
];

const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = reject;
    document.body.appendChild(script);
  });

export default function Checkout() {
  const { items, subtotal, shipping, tax, total, clear, updateQty } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(CHECKOUT_DRAFT_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      email: user?.email || "",
      phone: "",
      firstName: "",
      lastName: "",
      address: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      plannerName: "",
      saveAddress: true,
    };
  });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [checkoutStep, setCheckoutStep] = useState("summary");
  const [payment, setPayment] = useState("upi");
  const [placing, setPlacing] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponStatus, setCouponStatus] = useState("");
  const [pincodeStatus, setPincodeStatus] = useState("");
  const [pincodeDetails, setPincodeDetails] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const hasNamePersonalization = items.some((item) => item.personalization === "With Name on Cover");

  // Persist form draft automatically so signup/login round-trip doesn't lose data
  useEffect(() => {
    localStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(form));
  }, [form]);

  useEffect(() => {
    const hasVisitorInfo = [form.email, form.phone, form.firstName, form.lastName, form.address, form.city, form.pincode].some((value) => String(value || "").trim());
    if (!hasVisitorInfo) return undefined;

    const timer = window.setTimeout(async () => {
      try {
        const visitorId = form.email.trim().toLowerCase() || user?.uid || localStorage.getItem("noteconsole_visitor_id") || crypto.randomUUID();
        localStorage.setItem("noteconsole_visitor_id", visitorId);
        await setDoc(doc(db, "checkout_visitors", visitorId), {
          email: form.email.trim(),
          emailLower: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          address: form.address.trim(),
          landmark: form.landmark.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim(),
          country: form.country,
          plannerName: form.plannerName?.trim() || "",
          items: items.map(({ key, ...item }) => item),
          subtotal,
          source: "checkout",
          status: "checkout_started",
          updatedAt: serverTimestamp(),
        }, { merge: true });
      } catch (error) {
        console.error(error);
      }
    }, 900);

    return () => window.clearTimeout(timer);
  }, [form, items, subtotal, user?.uid]);

  // Sync email once user logs in
  useEffect(() => {
    if (user?.email && !form.email) {
      setForm((f) => ({ ...f, email: user.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  useEffect(() => {
    logEvent("checkout_view", { itemsCount: items.length, subtotal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updatePincode = (value) => {
    const pincode = value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode }));
  };

  const shippingCost = shipping;
  const preDiscountTotal = subtotal + shippingCost + tax;
  const discount = appliedCoupon
    ? appliedCoupon.type === "free_shipping"
      ? Math.min(preDiscountTotal, shippingCost)
      : Math.min(preDiscountTotal, Math.round(preDiscountTotal * ((appliedCoupon.discountPercent || 100) / 100)))
    : 0;
  const grandTotal = Math.max(0, preDiscountTotal - discount);

  useEffect(() => {
    if (form.pincode.length < 6) {
      setPincodeStatus("");
      setPincodeDetails(null);
      return;
    }

    const controller = new AbortController();
    const lookupPincode = async () => {
      setPincodeStatus("Checking PIN code...");
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${form.pincode}`, {
          signal: controller.signal,
        });
        const data = await response.json();
        const result = data?.[0];
        const postOffice = result?.PostOffice?.[0];

        if (result?.Status === "Success" && postOffice) {
          const offices = result.PostOffice || [];
          setForm((current) => ({
            ...current,
            city: current.city || postOffice.District || "",
            state: current.state || postOffice.State || "",
            country: postOffice.Country || "India",
          }));
          setPincodeDetails({
            district: postOffice.District,
            state: postOffice.State,
            country: postOffice.Country || "India",
            region: postOffice.Region,
            circle: postOffice.Circle,
            offices: offices.slice(0, 6).map((office) => office.Name).filter(Boolean),
          });
          setPincodeStatus(`PIN verified: ${postOffice.District}, ${postOffice.State}`);
        } else {
          setPincodeDetails(null);
          setPincodeStatus("Could not verify this PIN code");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
          setPincodeDetails(null);
          setPincodeStatus("PIN lookup unavailable. Please enter city and state manually.");
        }
      }
    };

    lookupPincode();
    return () => controller.abort();
  }, [form.pincode]);

  useEffect(() => {
    const cityQuery = form.city.trim();
    if (cityQuery.length < 1) {
      setCitySuggestions([]);
      return;
    }

    const normalizedQuery = normalizeText(cityQuery);
    const citySource = [...INDIA_CITY_STATE_LIST, ...INDIAN_CITIES];
    const suggestions = Array.from(
      new Map(
        citySource
          .map((item) => {
            const searchable = [item.city, item.state, ...(item.aliases || [])].map(normalizeText);
            const startsWith = searchable.some((value) => value.startsWith(normalizedQuery));
            const includes = searchable.some((value) => value.includes(normalizedQuery));
            return { ...item, startsWith, includes };
          })
          .filter((item) => item.startsWith || item.includes)
          .sort((a, b) => {
            if (a.startsWith !== b.startsWith) return a.startsWith ? -1 : 1;
            return a.city.localeCompare(b.city);
          })
          .map((item) => [`${item.city}-${item.state}`, item])
      ).values()
    ).slice(0, 10);

    setCitySuggestions(suggestions);
  }, [form.city]);

  const pincodeStateMismatch =
    Boolean(pincodeDetails?.state && form.state) &&
    normalizeText(pincodeDetails.state) !== normalizeText(form.state);

  const applyCouponWithCode = async (rawCode) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) return toast.error("Enter a coupon code");
    if (!form.email) return toast.error("Enter your email before applying a coupon");
    if (couponChecking) return;
    setCouponChecking(true);
    setCouponStatus(`Verifying ${code}...`);
    const promoCoupon = PROMO_COUPONS.find((coupon) => coupon.code === code);
    try {
      if (promoCoupon) {
        if (promoCoupon.firstOrderOnly) {
          const previousOrders = await getDocs(query(
            collection(db, "orders"),
            where("emailLower", "==", form.email.trim().toLowerCase()),
            limit(1)
          ));
          if (!previousOrders.empty) {
            setCouponStatus("NEWUSER15 is only valid on your first order.");
            toast.error("NEWUSER15 is only valid on your first order");
            return;
          }
        }
        setAppliedCoupon(promoCoupon);
        setCouponCode(code);
        setCouponStatus(`${promoCoupon.label} applied.`);
        toast.success(`${promoCoupon.label} applied`);
        return;
      }

      const validateCoupon = httpsCallable(functions, "validateCoupon");
      const result = await validateCoupon({
        code,
        email: form.email,
        preDiscountTotal,
      });
      const coupon = result.data;
      setAppliedCoupon(coupon);
      setCouponCode(code);
      const couponDiscount = Math.min(preDiscountTotal, Math.round(preDiscountTotal * ((coupon.discountPercent || 100) / 100)));
      setCouponStatus(couponDiscount >= preDiscountTotal ? "Coupon applied. Your order total is INR 0." : `${coupon.discountPercent || 100}% coupon applied.`);
      toast.success(couponDiscount >= preDiscountTotal ? "Coupon applied. Your order total is INR 0." : "Coupon applied");
    } catch (error) {
      console.error(error);
      setCouponStatus(error?.message?.replace("Firebase: ", "") || "Could not apply coupon.");
      toast.error("Could not apply coupon");
    } finally {
      setCouponChecking(false);
    }
  };
  const applyCoupon = () => applyCouponWithCode(couponCode);
  const continueToCheckout = () => {
    if (items.length === 0) return toast.error("Your cart is empty");
    if (hasNamePersonalization && !form.plannerName.trim()) return toast.error("Enter the name to print on the planner");
    setCheckoutStep("details");
    window.setTimeout(() => document.getElementById("checkout-details")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your cart is empty");
    if (!form.email || !form.phone || !form.firstName || !form.address || !form.city || !form.state || !form.pincode) {
      return toast.error("Please fill required fields");
    }
    if (hasNamePersonalization && !form.plannerName.trim()) return toast.error("Enter the name to print on the planner");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone number");
    if (!/^\d{6}$/.test(form.pincode)) return toast.error("Enter a valid 6-digit PIN code");
    if (pincodeStateMismatch) return toast.error("State and PIN code do not match");
    setPlacing(true);
    try {
      if (appliedCoupon?.firstOrderOnly) {
        const previousOrders = await getDocs(query(
          collection(db, "orders"),
          where("emailLower", "==", form.email.trim().toLowerCase()),
          limit(1)
        ));
        if (!previousOrders.empty) {
          toast.error(`${appliedCoupon.code} is only valid on your first order`);
          setPlacing(false);
          return;
        }
      }
      const orderData = {
        userId: user?.uid || "guest",
        email: form.email,
        emailLower: form.email.trim().toLowerCase(),
        items: items.map(({ key, ...rest }) => ({
          ...rest,
          plannerName: rest.personalization === "With Name on Cover" ? form.plannerName.trim() : "",
        })),
        shippingAddress: { ...form },
        shippingMethod,
        paymentMethod: payment,
        paymentStatus: grandTotal === 0 ? "coupon_paid" : "pending",
        couponCode: appliedCoupon?.code || "",
        couponDiscount: discount,
        subtotal,
        shipping: shippingCost,
        tax,
        preDiscountTotal,
        plannerName: hasNamePersonalization ? form.plannerName.trim() : "",
        total: grandTotal,
        status: "confirmed",
        createdAt: serverTimestamp(),
      };
      let orderId = "";
      if (appliedCoupon && grandTotal === 0) {
        const redeemCouponOrder = httpsCallable(functions, "redeemCouponOrder");
        const result = await redeemCouponOrder({
          couponCode: appliedCoupon.code,
          order: {
            ...orderData,
            createdAt: null,
          },
        });
        orderId = result.data.orderId;
        logEvent("order_placed", { orderId, total: grandTotal });
        localStorage.removeItem(CHECKOUT_DRAFT_KEY);
        clear();
        toast.success("Order placed successfully");
        navigate(`/orders?new=${orderId}`);
      } else {
        await loadRazorpayCheckout();
        const createRazorpayOrder = httpsCallable(functions, "createRazorpayOrder");
        const result = await createRazorpayOrder({
          order: {
            ...orderData,
            createdAt: null,
          },
        });
        const razorpayOrder = result.data;
        const verifyRazorpayPayment = httpsCallable(functions, "verifyRazorpayPayment");

        await new Promise((resolve, reject) => {
          const checkout = new window.Razorpay({
            key: razorpayOrder.keyId,
            amount: razorpayOrder.amount,
            currency: razorpayOrder.currency || "INR",
            name: "Note Console",
            description: "Planner order",
            image: "/favicon.png",
            order_id: razorpayOrder.razorpayOrderId,
            prefill: {
              name: `${form.firstName} ${form.lastName}`.trim(),
              email: form.email,
              contact: form.phone,
            },
            notes: {
              email: form.email,
              couponCode: appliedCoupon?.code || "",
            },
            theme: {
              color: "#3b281f",
            },
            handler: async (response) => {
              try {
                const verifyResult = await verifyRazorpayPayment({
                  order: {
                    ...orderData,
                    paymentMethod: "razorpay",
                    createdAt: null,
                  },
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                });
                resolve(verifyResult.data);
              } catch (error) {
                reject(error);
              }
            },
            modal: {
              ondismiss: () => reject(new Error("Payment cancelled")),
            },
          });
          checkout.on("payment.failed", (response) => {
            const failure = response?.error || {};
            logEvent("razorpay_payment_failed", {
              code: failure.code || "",
              description: failure.description || "",
              source: failure.source || "",
              step: failure.step || "",
              reason: failure.reason || "",
              razorpayOrderId: failure.metadata?.order_id || razorpayOrder.razorpayOrderId,
              razorpayPaymentId: failure.metadata?.payment_id || "",
              total: grandTotal,
            });
            reject(new Error(failure.description || failure.reason || "Payment failed"));
          });
          checkout.open();
        }).then((paidOrder) => {
          orderId = paidOrder.orderId;
          logEvent("order_placed", { orderId, total: grandTotal, paymentProvider: "razorpay" });
          localStorage.removeItem(CHECKOUT_DRAFT_KEY);
          clear();
          toast.success("Payment successful. Order placed.");
          navigate(`/orders?new=${orderId}`);
        });
      }
    } catch (err) {
      console.error(err);
      if (err.message === "Payment cancelled") {
        toast.error("Payment cancelled");
      } else if (/payment failed/i.test(err.message)) {
        toast.error("Payment failed. Please try another payment method.");
      } else {
        toast.error(err.message || "Could not place order. Please try again.");
      }
    } finally {
      setPlacing(false);
    }
  };

  const paymentOptions = [
    { id: "upi", label: "UPI", icon: Smartphone, desc: "Pay with any UPI app" },
    { id: "card", label: "Credit / Debit Card", icon: CreditCard, desc: "Visa, Mastercard, RuPay" },
    { id: "netbanking", label: "Net Banking", icon: Building, desc: "All major banks" },
    { id: "wallet", label: "Wallet", icon: Wallet, desc: "Wallets and supported apps" },
  ];

  return (
    <div data-testid="checkout-page" className="container-nc py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-serif text-4xl md:text-5xl text-espresso">Order Summary</h1>
        <p className="text-muted_brown mt-2">Review your planner, apply offers, and continue to secure checkout.</p>

        <section className="mt-8">
          <div className="bg-ivory border border-line rounded-xl p-6">
            {!user && (
              <div
                data-testid="guest-signup-banner"
                className="mb-6 bg-champagne/40 border border-line rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              >
                <div className="w-11 h-11 rounded-full gold-border flex items-center justify-center shrink-0">
                  <UserPlus size={16} strokeWidth={1.5} className="text-gold" />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-espresso">Save your details for next time</div>
                  <p className="text-sm text-muted_brown mt-1">Sign up or log in to track orders and finish future checkouts faster.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/signup?redirect=/checkout" data-testid="ck-signup-continue" className="btn-primary text-xs py-2.5 px-5">
                    <UserPlus size={13} /> Sign up
                  </Link>
                  <Link to="/login?redirect=/checkout" data-testid="ck-login-continue" className="btn-outline text-xs py-2.5 px-5">
                    <LogIn size={13} /> Log in
                  </Link>
                </div>
              </div>
            )}

            {items.length === 0 && (
              <p className="text-sm text-muted_brown">Your cart is empty. <Link to="/shop" className="underline">Continue shopping</Link>.</p>
            )}
            <div className="space-y-4">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 text-sm">
                  <div className="relative">
                    <img src={i.image} alt="" className="w-16 h-16 object-cover rounded-lg"/>
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-espresso text-cream text-[10px] flex items-center justify-center">{i.qty}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-espresso">{i.name}</div>
                    <div className="text-xs text-muted_brown leading-relaxed">
                      {[i.size, i.cover, i.personalization, i.pageColor, i.color].filter(Boolean).join(" - ")}
                    </div>
                    <div className="mt-2 inline-flex items-center rounded-full border border-line bg-cream overflow-hidden">
                      <button
                        type="button"
                        aria-label={`Decrease ${i.name} quantity`}
                        className="w-8 h-8 flex items-center justify-center text-espresso hover:bg-champagne/50"
                        onClick={() => updateQty(i.key, i.qty - 1)}
                      >
                        <Minus size={13} strokeWidth={1.8} />
                      </button>
                      <span className="min-w-8 px-2 text-center text-xs font-medium text-espresso">{i.qty}</span>
                      <button
                        type="button"
                        aria-label={`Increase ${i.name} quantity`}
                        className="w-8 h-8 flex items-center justify-center text-espresso hover:bg-champagne/50"
                        onClick={() => updateQty(i.key, i.qty + 1)}
                      >
                        <Plus size={13} strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                  <div className="text-espresso font-medium">{formatINR(i.priceINR * i.qty)}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 border-t border-line pt-5">
              <h2 className="font-serif text-2xl text-espresso mb-4">Name on Planner</h2>
              <input
                required={hasNamePersonalization}
                placeholder="Write the name exactly as it should appear on the cover"
                data-testid="ck-planner-name"
                className="input-nc"
                value={form.plannerName}
                onChange={(e) => update("plannerName", e.target.value)}
                maxLength={32}
              />
            </div>

            <div className="border-t border-line mt-5 pt-5 space-y-2 text-sm">
              <div className="flex justify-between text-muted_brown"><span>Subtotal</span><span className="text-espresso">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between text-muted_brown"><span>Shipping</span><span className="text-espresso">{shippingCost === 0 ? "Free" : formatINR(shippingCost)}</span></div>
              <div className="flex justify-between text-muted_brown"><span>Tax</span><span className="text-espresso">{formatINR(tax)}</span></div>
              {appliedCoupon && (
                <div className="flex justify-between text-muted_brown">
                  <span>Coupon ({appliedCoupon.code})</span>
                  <span className="text-espresso">-{formatINR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-line">
                <span className="font-serif text-lg text-espresso">Total</span>
                <span className="font-serif text-lg text-espresso" data-testid="ck-total">{formatINR(grandTotal)}</span>
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <input
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponStatus("");
                }}
                placeholder="Coupon code"
                data-testid="ck-coupon"
                className="input-nc text-sm"
              />
              <button type="button" disabled={couponChecking} onClick={applyCoupon} className="btn-outline px-4 py-2 text-xs disabled:opacity-60 disabled:cursor-wait" data-testid="ck-apply-coupon">
                {couponChecking ? "Verifying..." : "Apply"}
              </button>
            </div>
            {(couponChecking || couponStatus) && (
              <div className={`mt-2 text-xs ${appliedCoupon && couponStatus.includes("applied") ? "text-emerald-700" : couponChecking ? "text-muted_brown" : "text-red-600"}`} data-testid="ck-coupon-status">
                {couponChecking ? "Checking coupon validity and discount..." : couponStatus}
              </div>
            )}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PROMO_COUPONS.map((coupon) => (
                <button
                  key={coupon.code}
                  type="button"
                  disabled={couponChecking}
                  onClick={() => applyCouponWithCode(coupon.code)}
                  className={`text-left border rounded-xl px-3 py-2 transition-colors ${
                    appliedCoupon?.code === coupon.code
                      ? "border-espresso bg-cream text-espresso"
                      : "border-line text-muted_brown hover:border-espresso"
                  }`}
                >
                  <span className="block text-xs font-medium text-espresso">{coupon.label}</span>
                  <span className="block text-[11px] mt-0.5">{coupon.description}</span>
                </button>
              ))}
            </div>
            {appliedCoupon && grandTotal === 0 && (
              <div className="mt-3 rounded-xl border border-gold/50 bg-champagne/40 px-4 py-3 text-sm text-espresso">
                Coupon matched. Your payable amount is {formatINR(0)}. Place your free order below.
              </div>
            )}

            <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] text-muted_brown">
              <div className="p-2 border border-line rounded-lg text-center"><Truck size={14} className="mx-auto mb-1 text-espresso"/>Free ship</div>
              <div className="p-2 border border-line rounded-lg text-center"><ShieldCheck size={14} className="mx-auto mb-1 text-espresso"/>Secure pay</div>
              <div className="p-2 border border-line rounded-lg text-center"><Lock size={14} className="mx-auto mb-1 text-espresso"/>Encrypted</div>
            </div>

            <button
              type="button"
              data-testid="continue-checkout-btn"
              disabled={items.length === 0}
              onClick={continueToCheckout}
              className="btn-primary w-full mt-6 disabled:opacity-60"
            >
              {checkoutStep === "details" ? "Checkout Details Open" : "Continue to Checkout"}
            </button>
          </div>
        </section>

        {checkoutStep === "details" && (
          <form id="checkout-details" onSubmit={placeOrder} className="mt-12">
            <h2 className="font-serif text-4xl text-espresso">Secure Checkout</h2>
            <p className="text-muted_brown mt-2">Add address and payment details to place your order.</p>

            <section className="mt-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-2xl text-espresso">Contact Information</h2>
                {!user && (
                  <Link to="/login?redirect=/checkout" className="text-xs text-espresso underline">Already have an account? Log in</Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required type="email" placeholder="Email address" data-testid="ck-email" className="input-nc" value={form.email} onChange={(e) => update("email", e.target.value)} />
                <input required type="tel" placeholder="Phone number" data-testid="ck-phone" className="input-nc" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-serif text-2xl text-espresso mb-5">Shipping Address</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="First name" data-testid="ck-fname" className="input-nc" value={form.firstName} onChange={(e) => update("firstName", e.target.value)}/>
                <input placeholder="Last name" data-testid="ck-lname" className="input-nc" value={form.lastName} onChange={(e) => update("lastName", e.target.value)}/>
                <input required placeholder="Street address" data-testid="ck-address" className="input-nc sm:col-span-2" value={form.address} onChange={(e) => update("address", e.target.value)}/>
                <input placeholder="Apartment, landmark (optional)" data-testid="ck-landmark" className="input-nc sm:col-span-2" value={form.landmark} onChange={(e) => update("landmark", e.target.value)}/>
                <div className="relative">
                  <input
                    required
                    placeholder="City"
                    data-testid="ck-city"
                    className="input-nc"
                    value={form.city}
                    onFocus={() => setShowCitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 120)}
                    onChange={(e) => {
                      update("city", e.target.value);
                      setShowCitySuggestions(true);
                    }}
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-64 overflow-y-auto rounded-xl bg-[#1f1f1f] p-2 shadow-hover">
                      {citySuggestions.map((item) => (
                        <button
                          key={`${item.city}-${item.state}`}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setForm((current) => ({ ...current, city: item.city, state: item.state }));
                            setShowCitySuggestions(false);
                          }}
                          className="block w-full rounded-lg px-4 py-3 text-left text-cream hover:bg-white/10"
                        >
                          <span className="block font-medium">{item.city}</span>
                          <span className="mt-1 block text-xs text-cream/70">{item.state}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <select required data-testid="ck-state" className="input-nc" value={form.state} onChange={(e) => update("state", e.target.value)}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <div>
                  <input required inputMode="numeric" pattern="[0-9]{6}" placeholder="PIN code" data-testid="ck-pin" className="input-nc" value={form.pincode} onChange={(e) => updatePincode(e.target.value)}/>
                  {pincodeStatus && <div className="mt-2 text-xs text-muted_brown">{pincodeStatus}</div>}
                  {pincodeStateMismatch && <div className="mt-2 text-xs font-medium text-red-600">State and PIN code not matched.</div>}
                  {pincodeDetails && (
                    <div className="mt-3 rounded-xl border border-line bg-ivory p-4 text-xs text-muted_brown">
                      <div className="font-medium text-espresso">PIN details</div>
                      <div className="mt-2 grid gap-1">
                        <div>District: <span className="text-espresso">{pincodeDetails.district}</span></div>
                        <div>State: <span className="text-espresso">{pincodeDetails.state}</span></div>
                        {pincodeDetails.region && <div>Region: <span className="text-espresso">{pincodeDetails.region}</span></div>}
                        {pincodeDetails.offices?.length > 0 && (
                          <div>Post offices: <span className="text-espresso">{pincodeDetails.offices.join(", ")}</span></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <input placeholder="Country" data-testid="ck-country" className="input-nc" value={form.country} onChange={(e) => update("country", e.target.value)}/>
              </div>
              <label className="flex items-center gap-2 mt-4 text-sm text-espresso cursor-pointer">
                <input type="checkbox" checked={form.saveAddress} onChange={(e) => update("saveAddress", e.target.checked)} className="accent-espresso"/>
                Save this address for next time
              </label>
            </section>

            <section className="mt-10">
              <h2 className="font-serif text-2xl text-espresso mb-5">Shipping Method</h2>
              <div className="space-y-3">
                {[
                  { id: "standard", label: "Standard Shipping", desc: "5-7 days", price: shipping === 0 ? "Free" : formatINR(shipping) },
                ].map((s) => (
                  <label key={s.id} data-testid={`ship-${s.id}`} className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer ${shippingMethod === s.id ? "border-espresso bg-ivory" : "border-line"}`}>
                    <input type="radio" name="ship" checked={shippingMethod === s.id} onChange={() => setShippingMethod(s.id)} className="accent-espresso"/>
                    <div className="flex-1">
                      <div className="text-espresso font-medium">{s.label}</div>
                      <div className="text-xs text-muted_brown">{s.desc}</div>
                    </div>
                    <div className="text-espresso font-medium">{s.price}</div>
                  </label>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-serif text-2xl text-espresso mb-5">Payment Method</h2>
              <div className="space-y-3">
                {paymentOptions.map(({ id, label, icon: Icon, desc }) => (
                  <label key={id} data-testid={`pay-${id}`} className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer ${payment === id ? "border-espresso bg-ivory" : "border-line"}`}>
                    <input type="radio" name="pay" checked={payment === id} onChange={() => setPayment(id)} className="accent-espresso"/>
                    <Icon size={18} strokeWidth={1.5} className="text-espresso"/>
                    <div className="flex-1">
                      <div className="text-espresso font-medium">{label}</div>
                      <div className="text-xs text-muted_brown">{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-muted_brown">
                <Lock size={12}/> Your payment is secure and encrypted.
              </div>
            </section>

            <button
              type="submit"
              data-testid="place-order-btn"
              disabled={placing || items.length === 0}
              className="btn-primary w-full mt-10 disabled:opacity-60"
            >
              {placing ? "Placing order..." : grandTotal === 0 && appliedCoupon ? "Place Free Order" : "Place Order Securely"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
